import { useCallback, useRef } from 'react';
import { Box } from '@mui/material';
import { useTimelineContext, useTimelineRowGroup } from 'mq-timeline-calendar/react';
import { isTimeWithinBusinessHours } from '@/lib/inventoryUtils';
import { UTILIZATION_ROW_ID } from '@/features/calendar/calendarUtilization';
import type { CalendarHours } from '@/hooks/useTenantSettings';

interface CalendarClickOverlayProps {
  calendarHours: CalendarHours;
  onEmptyClick: (clickedDate: Date, mechanicId: string | null) => void;
}

function roundTo30Min(date: Date): Date {
  const rounded = new Date(date);
  rounded.setMinutes(Math.round(rounded.getMinutes() / 30) * 30, 0, 0);
  return rounded;
}

/**
 * Single overlay placed inside TimelineRowGroup that captures clicks on
 * empty areas. Uses engine.pixelToTime for X → timestamp and
 * rowGroup.getRowAtPixelY for Y → mechanic row resolution.
 */
export function CalendarClickOverlay({
  calendarHours,
  onEmptyClick,
}: CalendarClickOverlayProps) {
  const { engine } = useTimelineContext();
  const rowGroup = useTimelineRowGroup();
  const containerRef = useRef<HTMLDivElement>(null);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!engine || !containerRef.current || !rowGroup) return;

      // Only respond to direct clicks on the overlay, not on timeline items above it
      if (e.target !== e.currentTarget) return;

      const rect = containerRef.current.getBoundingClientRect();
      const offsetX = e.clientX - rect.left;
      const offsetY = e.clientY - rect.top;

      // Resolve time from X
      const timestamp = engine.pixelToTime(offsetX);
      const clickedDate = roundTo30Min(new Date(timestamp));

      if (!isTimeWithinBusinessHours(clickedDate, calendarHours)) return;

      // Resolve row from Y
      const rowInfo = rowGroup.getRowAtPixelY(offsetY);
      if (!rowInfo || rowInfo.isInHeader || rowInfo.rowId === UTILIZATION_ROW_ID) return;

      const mechanicId = rowInfo.rowId === 'unassigned' ? null : rowInfo.rowId;

      onEmptyClick(clickedDate, mechanicId);
    },
    [engine, rowGroup, calendarHours, onEmptyClick],
  );

  return (
    <Box
      ref={containerRef}
      onClick={handleClick}
      // Ignore in mq-timeline-calendar content-height measurement: a stretch
      // overlay would force minHeight ≈ viewport and leave a permanent few-px
      // vertical scrollbar (see library patch on content extent).
      data-timeline-measure-ignore
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 0,
        cursor: 'pointer',
      }}
    />
  );
}
